'use client'

import { Fragment } from 'react'
import { motion } from 'framer-motion'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import type {
  PricingTier,
  PricingComparisonGroup,
} from '@/content/data/pricing-hub'

interface PricingComparisonTableProps {
  tiers: PricingTier[]
  variant?: 'primary' | 'ai'
}

function getTierValue(
  tier: PricingTier,
  groupName: string,
  rowLabel: string
): string | undefined {
  return tier.comparisonGroups
    ?.find((g) => g.name === groupName)
    ?.rows.find((r) => r.label === rowLabel)?.value
}

function renderValue(value: string) {
  if (value === '✓' || value.toLowerCase() === 'yes') {
    return (
      <Check
        className={cn(
          'w-5 h-5 mx-auto',
          'text-brand-green'
        )}
      />
    )
  }
  if (value === '—' || value === '-' || value.toLowerCase() === 'none') {
    return (
      <span className={'text-muted'}>—</span>
    )
  }
  if (value.includes('<')) {
    return (
      <span
        className={'text-muted'}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    )
  }
  return value
}

export function PricingComparisonTable({
  tiers,
  variant = 'primary',
}: PricingComparisonTableProps) {
  const isAi = variant === 'ai'
  const groups = tiers[0]?.comparisonGroups ?? []

  if (groups.length === 0) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
      className="mt-16 max-w-6xl mx-auto"
    >
      <div
        className={cn(
          'rounded-2xl border overflow-hidden',
          isAi
            ? 'bg-panel border-line'
            : 'bg-panel border-line'
        )}
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr
                className={cn(
                  'border-b',
                  'border-line'
                )}
              >
                <th
                  className={cn(
                    'text-left px-6 py-4 font-heading font-semibold text-lg sticky left-0 z-10',
                    'text-ink bg-panel'
                  )}
                >
                  What&apos;s included
                </th>
                {tiers.map((tier) => (
                  <th
                    key={tier.id}
                    className={cn(
                      'px-4 py-4 text-center min-w-[160px]',
                      'text-ink'
                    )}
                  >
                    <div className="font-heading font-semibold text-base">
                      {tier.name}
                    </div>
                    {tier.popular && (
                      <span
                        className={cn(
                          'text-[10px] font-semibold px-2 py-0.5 rounded-full mt-1 inline-block',
                          isAi
                            ? 'bg-brand-violet-soft text-brand-violet'
                            : 'bg-brand-green-soft text-brand-green'
                        )}
                      >
                        Most Popular
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((group: PricingComparisonGroup) => (
                <Fragment key={group.name}>
                  <tr>
                    <td
                      colSpan={tiers.length + 1}
                      className={cn(
                        'px-6 py-3 text-sm font-semibold uppercase tracking-wider',
                        isAi
                          ? 'bg-panel/50 text-muted'
                          : 'bg-page text-muted'
                      )}
                    >
                      {group.name}
                    </td>
                  </tr>

                  {group.rows.map((row, rowIndex) => (
                    <tr
                      key={`${group.name}-${row.label}-${rowIndex}`}
                      className={cn(
                        'border-b last:border-b-0',
                        'border-line'
                      )}
                    >
                      <td
                        className={cn(
                          'px-6 py-4 sticky left-0 z-10 min-w-[220px]',
                          isAi
                            ? 'text-ink bg-panel'
                            : 'text-ink bg-panel'
                        )}
                      >
                        <div className="font-medium text-sm">{row.label}</div>
                        {row.sublabel && (
                          <div
                            className={cn(
                              'text-xs mt-0.5',
                              'text-muted'
                            )}
                          >
                            {row.sublabel}
                          </div>
                        )}
                      </td>
                      {tiers.map((tier) => {
                        const value =
                          getTierValue(tier, group.name, row.label) ?? '—'
                        return (
                          <td
                            key={`${tier.id}-${row.label}`}
                            className={cn(
                              'px-4 py-4 text-center text-sm min-w-[160px]',
                              'text-muted'
                            )}
                          >
                            {renderValue(value)}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  )
}
